import { useState, useEffect } from 'react';
import UserService from '../../services/UserService';
import Notifications from '../../services/Notifications';
import UserHelper from '../../helpers/UserHelper';

const useUserDetails = ({
    userId,
    currentUser,
    usersToClone,
    userActions,
    refreshFilteredUsers,
    StorefrontConfig,
    setSelectedStorefront,
    userSettings
}) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [secondaryEmail, setSecondaryEmail] = useState('');
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [hasAssignedBranches, setHasAssignedBranches] = useState(false);
    const [modals, setModals] = useState({
        secondaryEmail: false,
        storefront: false,
        warranty: false,
        branches: false,
        clone: false
    });

    const [persona, setPersona] = useState({});
    const [storefront, setStorefront] = useState({});
    const [branches, setBranches] = useState({
        assigned: [],
        available: [],
        modal: []
    });

    const [selectedStorefront, setSelectedStorefrontState] = useState({});
    const [selectedPersona, setSelectedPersona] = useState({});
    const [cloneProcessing, setCloneProcessing] = useState(false);
    const [updateUsersToClone, setUpdateUsersToClone] = useState([]);
    const [isRoleClone, setIsRoleClone] = useState(false);
    const localized = StorefrontConfig.labels;
    const salesTerritorySelector = userSettings?.SalesTerritorySelector ?? false;

    useEffect(() => {
        const loadUser = async () => {
            setLoading(true);
            const res = await UserService.getUserByid(userId);
            const defaultStorefront = StorefrontConfig.getStorefrontByNumber(res?.storefrontNumber || '09000');

            setUser(res);
            setStorefront(defaultStorefront);
            setPersona({
                value: res.persona?.id || '',
                label: res.persona?.displayName || ''
            });
            setSecondaryEmail(res.secondaryEmail);
            setHasAssignedBranches(res.roles.includes("HasAssignedBranches"));
            setBranches({
                assigned: res.assignedBranches || [],
                modal: res.assignedBranches || [],
                available: defaultStorefront.businessUnits?.filter(x => x.shown) || []
            });
            setLoading(false);
        };

        loadUser();
    }, [userId]);

    const toggleIsActive = () => {
        UserService.updateActive(user.id, () => {
            const updatedUser = { ...user, isActive: !user.isActive };
            userActions.setSelectedUser(updatedUser);
            setUser(updatedUser);
        });
    };

    const updateFullNameState = (fullName) => {
        const updatedUser = { ...user, ...fullName };
        setUser(updatedUser);
        refreshFilteredUsers();
    };

    const openSecondaryEmailModal = () => setModals((m) => ({ ...m, secondaryEmail: true }));
    const closeSecondaryEmailModal = () => setModals((m) => ({ ...m, secondaryEmail: false }));

    const updateSecondaryEmail = (e) => {
        const value = e.currentTarget.value;
        setSecondaryEmail(value);
        setIsValidEmail(!value || UserHelper.isValidEmail(value));
    };

    const saveSecondaryEmail = () => {
        user.secondaryEmail = secondaryEmail;
        userActions.setSelectedUser(user);
        UserService.setSecondaryEmailApi(user.id, secondaryEmail);
        setModals((m) => ({ ...m, secondaryEmail: false }));
    };

    const removeSecondaryEmail = () => {
        Notifications.confirmation(localized.ConfirmRemoveSecondaryEmail, () => {
            user.secondaryEmail = '';
            setSecondaryEmail('');
            userActions.setSelectedUser(user);
            UserService.setSecondaryEmailApi(user.id, '');
        });
    };

    const openCloneModal = () => setModals((m) => ({ ...m, clone: true }));
    const closeCloneModal = () => {
        setModals((m) => ({ ...m, clone: false }));
        setCloneProcessing(false);
        setIsRoleClone(false);
        setUpdateUsersToClone([]);
    };

    const handleCloneSave = async () => {
        setCloneProcessing(true);

        try {
            const response = await UserService.cloneUser(
                user.id,
                updateUsersToClone,
                isRoleClone
            );
            if (response) {
                const json = JSON.parse(response);
                if (json.IsSuccess) {
                    Notifications.message(localized.SuccessUserCloned);
                    closeCloneModal();
                    refreshFilteredUsers();
                    await reloadUser();
                } else {
                    Notifications.error(localized.FailedUserCloned);
                }
            }
        } catch (error) {
            Notifications.error(localized.ErrorUserCloned, error);
        } finally {
            setCloneProcessing(false);
        }
    };

    const reloadUser = () => {
        if (user?.id) {
            UserService.getUserByid(user.id).then(setUser);
        }
    };

    const openStorefrontModal = () => setModals((m) => ({ ...m, storefront: true }));
    const closeStorefrontModal = () => setModals((m) => ({ ...m, storefront: false }));

    const openBranchModal = () => setModals((m) => ({ ...m, branches: true }));
    const closeBranchModal = () => setModals((m) => ({ ...m, branches: false }));

    const setModalBranches = (branchesList) => {
        setBranches((prev) => ({
            ...prev,
            modal: branchesList
        }));
    };

    const saveBranches = async () => {
        await UserService.updateAssignedBranches(user.id, branches.modal);
        setBranches((prev) => ({
            ...prev,
            assigned: [...prev.modal]
        }));
        closeBranchModal();

        if (user.id === currentUser.id) {
            const updatedUser = { ...user, assignedBranches: [...branches.modal] };
            userActions.setSelectedUser(updatedUser);
            userActions.ReceivedCurrentUser?.(updatedUser);
        }
    };
    const cancelBranchModal = () => {
        setBranches((prev) => ({
            ...prev,
            modal: [...prev.assigned]
        }));
        closeBranchModal();
    };

    const [updateWarrantyRoles, setUpdateWarrantyRoles] = useState([]);
    const openWarrantyModal = () => setModals((m) => ({ ...m, warranty: true }));
    const closeWarrantyModal = () => setModals((m) => ({ ...m, warranty: false }));

    const saveWarrantyAccess = async () => {
        await UserService.updateWarrantyAccess(user.id, updateWarrantyRoles, async () => {
            const updatedUser = await UserService.getUserByid(user.id);
            setUser(updatedUser);
            userActions.setSelectedUser(updatedUser);
        });
        closeWarrantyModal();
    };

    const setStorefrontPersona = (storefront, persona) => {
        setSelectedStorefrontState(storefront);
        setSelectedPersona(persona);
    };

    const saveStorefrontPersona = async () => {
        if (selectedPersona?.label && selectedStorefront?.label) {
            closeStorefrontModal();
            const storefront = StorefrontConfig.getStorefrontById(selectedStorefront.value);

            // Update storefront
            await UserService.updateStorefrontApi(user.email, storefront.number);
            user.storefrontNumber = storefront.number;
            userActions.setSelectedUser(user);
            setStorefront(storefront);
            setSelectedStorefront(user.storefrontNumber);

            // Update persona 
            await updatePersona(selectedPersona);
            setPersona(selectedPersona);
        } else {
            Notifications.error(localized.ErrorStorefrontAndPersona);
        }
    };

    const updatePersona = async (changePersona) => {
        await UserService.updatePersonaApi(user.id, changePersona.value);
        user.personaName = changePersona.name || selectedPersona.label;
        const rolesOnly = changePersona.roles.map(role => role.role.name);
        setHasAssignedBranches(rolesOnly.includes("HasAssignedBranches"));
        userActions.setSelectedUser(user);
    };

    return {
        user,
        loading,
        modals,
        persona,
        storefront,
        branches,
        handlers: {
            toggleIsActive,
            updateFullNameState,
            openSecondaryEmailModal,
            closeSecondaryEmailModal,
            updateSecondaryEmail,
            saveSecondaryEmail,
            removeSecondaryEmail,
            secondaryEmail,
            isValidEmail,
            openCloneModal,
            closeCloneModal,
            handleCloneSave,
            cloneProcessing,
            isRoleClone,
            toggleRoleClone: () => setIsRoleClone((prev) => !prev),
            setUsersToClone: setUpdateUsersToClone,
            updateUsersToClone,
            usersToClone,
            openWarrantyModal,
            closeWarrantyModal,
            openBranchModal,
            closeBranchModal,
            cancelBranchModal,
            setModalBranches,
            saveBranches,
            saveWarrantyAccess,
            setWarrantyRoles: setUpdateWarrantyRoles,
            openStorefrontModal,
            closeStorefrontModal,
            saveStorefrontPersona,
            setStorefrontPersona,
            hasAssignedBranches
        },
        localized,
        salesTerritorySelector
    };
};

export default useUserDetails;
